import type Guid from "@/app/types/common/guid";
import type { Maybe } from "@/components/utils/typeHelpers";
import { debounce } from "lodash-es";
import { shallowRef } from "vue";

export interface MapMarkerHoverLine {
  text: string;
  url?: Maybe<string>;
}

export interface MapMarker<Meta = any> {
  key: string | null;
  name: string;
  lat: number | null;
  lng: number | null;
  sequence?: number;
  pinLabelPrefix?: string;
  colour?: string;
  icon?: MapIcon;
  useSvg?: boolean;
  meta?: Meta;
  showSequence?: boolean;
  includeInMapBounds?: boolean;
  hoverLines?: MapMarkerHoverLine[];
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface Polyline {
  id: Guid;
  encodedPolyline: string;
  strokeColor?: string; //Only Hex Values eg. '#00008B'
  strokeOpacity?: number;
  strokeWeight?: number;
  zIndex?: number;
}

export type MapIcon = keyof typeof mapIcons;

export const mapIcons = {
  blue: "//maps.google.com/mapfiles/ms/icons/blue-dot.png",
  red: "//maps.google.com/mapfiles/ms/icons/red-dot.png",
  green: "//maps.google.com/mapfiles/ms/icons/green-dot.png",
  pink: "//maps.google.com/mapfiles/ms/icons/pink-dot.png",
  purple: "//maps.google.com/mapfiles/ms/icons/purple-dot.png",
  orange: "//maps.google.com/mapfiles/ms/icons/orange-dot.png",
} as const;

export const bluePinColour = "#5596E6" as const;
export const redPinColour = "#EA4335" as const;
export const orangePinColour = "#FFB800" as const;

export const useMapMarkers = <T = any, Meta = any>(
  itemToMarkerConverter: (item: Readonly<T>) => MapMarker<Meta>,
  items: (() => readonly T[]) | readonly T[]
) => {
  const mapMarkers = shallowRef<MapMarker<Meta>[]>([]);
  const updateMapMarkers = () => {
    const itemsArray = typeof items === "function" ? items() : items;

    // Be explicit with using item, as map will also pass through idx
    mapMarkers.value = itemsArray.map((item) => itemToMarkerConverter(item));
  };

  const updateMapMarkersDebounced = debounce(updateMapMarkers, 300);

  return {
    mapMarkers,
    updateMapMarkers: updateMapMarkersDebounced,
  };
};
